import { Switch } from '@components/Switch/Switch';
import { GradientScrollDiv } from '@components/util/GradientScrollDiv';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Checkbox,
  Chip,
  FormControlLabel,
  Popover,
  Radio,
  RadioGroup,
  Tooltip,
} from '@mui/material';
import { RefObject } from 'react';
import css from './MediaDataGrid.module.scss';

type TimePeriodFilter = 'lastWeek' | 'lastMonth' | 'lastYear' | null;

interface MediaDataGridFilterPopoverProps {
  isOpen: boolean;
  anchorEl: HTMLButtonElement | null;
  onClose: () => void;
  // Time period filter
  getCurrentTimePeriod: () => string;
  onTimePeriodClick: (period: TimePeriodFilter) => void;
  onAnyTimeClick: () => void;
  // Format filters (Shorts / Long form) — YouTube-specific, so only shown when
  // the collection actually contains YouTube videos.
  showFormatFilter: boolean;
  showShorts: boolean;
  showLongForm: boolean;
  onFormatChange: (type: 'shorts' | 'longForm', checked: boolean) => void;
  // Show in-library filter
  showInLibrary: boolean;
  onShowInLibraryChange: (checked: boolean) => void;
  // Title contains filter
  titleContainsChips: string[];
  titleContainsInput: string;
  titleFilterMode: 'any' | 'all';
  onTitleContainsInputChange: (value: string) => void;
  onTitleFilterModeChange: () => void;
  onTitleInputKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onTitleInputPaste: (e: React.ClipboardEvent<HTMLInputElement>) => void;
  onTitleInputBlur: () => void;
  onDeleteTitleChip: (chip: string) => void;
  titleInputRef: RefObject<HTMLInputElement | null>;
  titleChipsContainerRef: RefObject<HTMLDivElement | null>;
  // Clear filters
  onClearFilters: () => void;
}

export const MediaDataGridFilterPopover = ({
  isOpen,
  anchorEl,
  onClose,
  getCurrentTimePeriod,
  onTimePeriodClick,
  onAnyTimeClick,
  showFormatFilter,
  showShorts,
  showLongForm,
  onFormatChange,
  showInLibrary,
  onShowInLibraryChange,
  titleContainsChips,
  titleContainsInput,
  titleFilterMode,
  onTitleContainsInputChange,
  onTitleFilterModeChange,
  onTitleInputKeyDown,
  onTitleInputPaste,
  onTitleInputBlur,
  onDeleteTitleChip,
  titleInputRef,
  titleChipsContainerRef,
  onClearFilters,
}: MediaDataGridFilterPopoverProps) => {
  return (
    <Popover
      open={isOpen}
      anchorEl={anchorEl}
      onClose={onClose}
      transitionDuration={100}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      slotProps={{
        paper: {
          className: css.filtersPopover,
        },
      }}
    >
      <div className={css.filtersPopoverContent}>
        <div className={css.filtersPopoverHeader}>FILTERS</div>

        <GradientScrollDiv
          className={css.filtersScrollContainer}
          innerDivClassName={css.filtersScrollInner}
        >
          <div className={css.filtersSection}>
            <div className={css.filtersSectionLabel}>Time range</div>
            <RadioGroup
              value={getCurrentTimePeriod()}
              onChange={(e) => {
                const value = e.target.value;
                if (value === 'anyTime') {
                  onAnyTimeClick();
                } else {
                  onTimePeriodClick(value as TimePeriodFilter);
                }
              }}
              className={css.filtersRadioGroup}
            >
              <FormControlLabel
                value="anyTime"
                control={<Radio className={css.filtersRadio} />}
                label="Any time"
                className={css.filtersFormControlLabel}
              />
              <FormControlLabel
                value="lastWeek"
                control={<Radio className={css.filtersRadio} />}
                label="Last week"
                className={css.filtersFormControlLabel}
              />
              <FormControlLabel
                value="lastMonth"
                control={<Radio className={css.filtersRadio} />}
                label="Last month"
                className={css.filtersFormControlLabel}
              />
              <FormControlLabel
                value="lastYear"
                control={<Radio className={css.filtersRadio} />}
                label="Last year"
                className={css.filtersFormControlLabel}
              />
            </RadioGroup>
          </div>

          {showFormatFilter && (
            <div className={css.filtersSection}>
              <div className={css.filtersSectionLabel}>Format</div>
              <div className={css.filtersCheckboxGroup}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={showShorts}
                      onChange={(e) => onFormatChange('shorts', e.target.checked)}
                      className={css.filtersCheckbox}
                    />
                  }
                  label="Shorts"
                  className={css.filtersFormControlLabel}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={showLongForm}
                      onChange={(e) => onFormatChange('longForm', e.target.checked)}
                      className={css.filtersCheckbox}
                    />
                  }
                  label="Long form"
                  className={css.filtersFormControlLabel}
                />
              </div>
            </div>
          )}

          {/* TODO: Add aspect ratio filter section */}

          <div className={css.filtersSection}>
            <div className={css.filtersSwitchRow}>
              <span className={css.filtersSwitchLabel}>Show in-library</span>
              <Tooltip
                title="When switched on, you will see media found in the URLs that are already in your library"
                placement="top"
                arrow
              >
                <InfoOutlinedIcon className={css.filtersInfoIcon} />
              </Tooltip>
              <Switch checked={showInLibrary} onChange={onShowInLibraryChange} />
            </div>
          </div>

          <div className={css.filtersSection}>
            <div className={css.filtersSectionLabel}>
              Title contains{' '}
              <span className={css.filterModeToggle} onClick={onTitleFilterModeChange}>
                {titleFilterMode === 'any' ? 'any of' : 'all of'}
              </span>{' '}
              the following:
            </div>
            <div
              ref={titleChipsContainerRef}
              className={css.titleChipsContainer}
              onClick={() => titleInputRef.current?.focus()}
            >
              {titleContainsChips.map((chip) => (
                <Chip
                  key={chip}
                  label={chip}
                  onDelete={() => onDeleteTitleChip(chip)}
                  size="small"
                  className={css.titleChip}
                />
              ))}
              <input
                ref={titleInputRef}
                type="text"
                value={titleContainsInput}
                onChange={(e) => onTitleContainsInputChange(e.target.value)}
                onKeyDown={onTitleInputKeyDown}
                onPaste={onTitleInputPaste}
                onBlur={onTitleInputBlur}
                placeholder={titleContainsChips.length === 0 ? 'Add filter...' : ''}
                className={css.titleChipInput}
              />
            </div>
          </div>
        </GradientScrollDiv>

        <div className={css.filtersResetSection}>
          <span className={css.filtersResetButton} onClick={onClearFilters}>
            Reset filters
          </span>
        </div>
      </div>
    </Popover>
  );
};
